import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search } from 'lucide-react';

interface SearchBarProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isSearching: boolean;
  isSearchFocused: boolean;
  setIsSearchFocused: (focused: boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
  placeholder: string;
  logoSrc: string;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  children?: React.ReactNode; // For search results dropdown
}

/**
 * SearchBar Component
 * Handles the search input field with loading animation
 */
const SearchBar: React.FC<SearchBarProps> = ({
  searchQuery,
  setSearchQuery,
  isSearching,
  isSearchFocused,
  setIsSearchFocused,
  onSubmit,
  placeholder,
  logoSrc,
  searchInputRef,
  children
}) => {
  return (
    <motion.div
      className="w-full mb-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.7 }}
    >
      <form onSubmit={onSubmit} className="relative">
        <div className="relative">
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
            placeholder={placeholder}
            className="w-full px-6 py-3.5 pr-12 text-lg border border-gray-300 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-md focus:outline-none focus:shadow-lg transition-all duration-300 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
            autoFocus
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-all"
          >
            <AnimatePresence mode="wait">
              {isSearching ? (
                <motion.img
                  key="loading"
                  src={logoSrc}
                  alt="Loading"
                  className="h-6 w-6 object-contain"
                  initial={{ opacity: 0, scale: 0.5, rotate: -180 }}
                  animate={{
                    opacity: 1,
                    scale: [1, 1.1, 1],
                    rotate: 0
                  }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  transition={{
                    scale: {
                      duration: 1,
                      repeat: Infinity,
                      ease: "easeInOut"
                    },
                    opacity: { duration: 0.2 },
                    rotate: { duration: 0.3 }
                  }}
                />
              ) : (
                <motion.div
                  key="search-icon"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.15 }}
                >
                  <Search className="h-6 w-6" />
                </motion.div>
              )}
            </AnimatePresence>
          </button>
        </div>

        {/* Children will render search results/favorites/no results */}
        {children}
      </form>
    </motion.div>
  );
};

export default SearchBar;
